import {
  type SimulationListEntryDto,
  Permission,
  SimulationStatus,
} from '@/api';
import PermissionWrapper from '@/components/auth/permission-wrapper';
import BulkActionsBar from '@/components/table/bulk-actions-bar';
import { useNotification } from '@/hooks/use-notification';
import { useSimulationErrorNotification } from '@/hooks/use-simulation-error-notification';
import {
  useDeleteSimulation,
  useRenameSimulation,
} from '@/pages/price-planning/simulation/simulation-queries';
import { Button, Tooltip } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import { useQueryClient } from '@tanstack/react-query';
import type { MRT_RowSelectionState } from 'material-react-table';
import { useRef, useState } from 'react';
import { SIMULATION_LIST_QUERY_KEY } from '../../simulation-list-page';
import { DeleteSimulationModal } from '../delete-simulation-modal';
import { RenameSimulationModal } from '../rename-simulation-modal';

enum ModalType {
  Delete = 'delete',
  Rename = 'rename',
}

type RowSelection = {
  state: MRT_RowSelectionState;
  items: SimulationListEntryDto[];
};

type SimulationListBulkActionsBarProps = {
  rowSelection: RowSelection;
  setRowSelection: (selection: RowSelection) => void;
};

const SimulationListBulkActionsBar = ({
  rowSelection,
  setRowSelection,
}: SimulationListBulkActionsBarProps) => {
  const queryClient = useQueryClient();
  const showNotification = useNotification();
  const showSimulationError = useSimulationErrorNotification();
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const renameTriggerRef = useRef<HTMLButtonElement | null>(null);

  const [showModal, setShowModal] = useState<ModalType | null>(null);
  const invalidateSimulationList = () =>
    queryClient.invalidateQueries({ queryKey: [SIMULATION_LIST_QUERY_KEY] });

  const { mutateAsync: deleteSimulation, isPending: isDeletingSimulation } =
    useDeleteSimulation(
      () => {
        invalidateSimulationList();
        showNotification({
          title: 'Simulation Deleted',
          description: 'The simulation has been deleted successfully.',
          type: 'success',
          duration: 5_000,
        });
        setShowModal(null);
      },
      (error) => {
        showSimulationError(error, 'delete simulation');
      },
    );

  const { mutateAsync: renameSimulation } = useRenameSimulation(
    () => {
      invalidateSimulationList();
      showNotification({
        title: 'Simulation Renamed',
        description: 'The simulation has been renamed successfully.',
        type: 'success',
        duration: 5_000,
      });
      setShowModal(null);
    },
    (error) => {
      showSimulationError(error, 'rename simulation');
    },
  );

  const deleteButton = (
    <Button
      aria-label="Delete"
      iconName="trash-solid"
      iconPosition="start"
      size={'small'}
      ref={deleteTriggerRef}
      style={{ backgroundColor: 'transparent', padding: 0 }}
      onClick={() => setShowModal(ModalType.Delete)}
      disabled={rowSelection.items.some(
        (item) => item.status === SimulationStatus.Published,
      )}
    >
      Delete
    </Button>
  );

  const deselectAll = () => setRowSelection({ state: {}, items: [] });

  return (
    <>
      <BulkActionsBar
        selectedItemsCount={rowSelection.items.length}
        onDeselectAll={deselectAll}
        position="absolute"
        actions={
          <Flex gap={'16px'} align={'center'}>
            <PermissionWrapper
              requiredPermissions={[Permission.DeleteSimulations]}
            >
              {rowSelection.items.some(
                (item) => item.status === SimulationStatus.Published,
              ) ? (
                <Tooltip>
                  <Tooltip.Trigger>{deleteButton}</Tooltip.Trigger>
                  <Tooltip.Content>
                    {'Cannot delete published simulations.'}
                  </Tooltip.Content>
                </Tooltip>
              ) : (
                deleteButton
              )}
            </PermissionWrapper>
            <PermissionWrapper
              requiredPermissions={[Permission.WriteSimulations]}
            >
              {rowSelection.items.length === 1 && (
                <Button
                  aria-label="Rename"
                  iconName="text"
                  iconPosition="start"
                  size={'small'}
                  ref={renameTriggerRef}
                  style={{
                    backgroundColor: 'transparent',
                    padding: '0',
                  }}
                  onClick={() => setShowModal(ModalType.Rename)}
                  disabled={
                    rowSelection.items[0]?.status !== SimulationStatus.Draft
                  }
                >
                  Rename
                </Button>
              )}
            </PermissionWrapper>
          </Flex>
        }
      />
      <DeleteSimulationModal
        data={rowSelection.items}
        triggerRef={deleteTriggerRef}
        onClose={() => setShowModal(null)}
        isOpen={showModal === ModalType.Delete}
        isDeleting={isDeletingSimulation}
        onConfirmDelete={() => {
          deleteSimulation(rowSelection.items.map((item) => item.simulationId));
          deselectAll();
        }}
      />
      {rowSelection.items[0] && (
        <RenameSimulationModal
          triggerRef={renameTriggerRef}
          currentName={rowSelection.items[0].simulationName}
          onClose={() => setShowModal(null)}
          isOpen={showModal === ModalType.Rename}
          onSubmit={(newName: string) => {
            rowSelection.items[0] &&
              renameSimulation({
                simulationId: rowSelection.items[0].simulationId,
                newName,
              });
            deselectAll();
          }}
        />
      )}
    </>
  );
};
export default SimulationListBulkActionsBar;
